import { FC, ReactNode, useEffect, useState, useMemo } from "react";
import Heading from "components/Heading/Heading";
import HeaderFilter from "containers/Home/HeaderFilter";
import MultiStockCard from "components/Quasi/MultiStockCard/MultiStockCard";
import Skeleton from "react-loading-skeleton";
import { useGetHighlightsQuery } from "application/features/property/propertyApiSlice";

export interface SectionGridFeaturePlacesProps {
  gridClass?: string;
  heading?: ReactNode;
  subHeading?: ReactNode;
  headingIsCenter?: boolean;
}

const SectionGridFeaturePlaces: FC<SectionGridFeaturePlacesProps> = ({
  gridClass = "",
  heading,
  subHeading,
  headingIsCenter,
}) => {
  const { data: highlights, isLoading: loadingHighlights } = useGetHighlightsQuery({});
  const [activeTab, setActiveTab] = useState("");

  // Extract unique destinations from properties
  const destinations = useMemo(() => {
    if (!highlights?.properties) return [];
    const allDestinations = highlights.properties
      .map((stay: any) => stay.borgo_name)
      .filter(Boolean);
    return allDestinations.filter(
      (dest: string, index: number) => allDestinations.indexOf(dest) === index
    );
  }, [highlights]);

  useEffect(() => {
    if (destinations.length > 0 && !activeTab) {
      setActiveTab(destinations[0] as string);
    }
  }, [destinations, activeTab]);

  // Filter properties by selected destination, excluding in-review listings
  const filteredProperties = useMemo(() => {
    if (!highlights?.properties) return [];
    const publicListings = highlights.properties.filter(
      (stay: any) => !stay.in_review
    );
    if (!activeTab) return publicListings;
    return publicListings.filter(
      (stay: any) => stay.borgo_name === activeTab
    );
  }, [highlights, activeTab]);

  const renderCard = (stay: any) => {
    return (
      <MultiStockCard
        key={stay.property_uuid}
        cover={stay.cover}
        images={stay.media || []}
        id={stay.property_uuid}
        title={stay.name}
        mood={stay.mood}
        description={null}
        annotation={stay.annotation}
        address={stay.address}
        price={stay.weekday_price}
        salePrice={stay.salePrice}
        orientation="vertical"
        draggable={false}
      />
    );
  };

  const renderCardSkeleton = () => (
    <div className="w-full">
      <Skeleton height={250} className="rounded-t-2xl" />
      <div className="p-4 space-y-4">
        <div className="space-y-2">
          <Skeleton width={120} height={16} />
          <Skeleton height={24} />
          <Skeleton height={16} />
        </div>
        <div className="flex justify-between items-center">
          <Skeleton width={100} height={20} />
          <Skeleton width={80} height={16} />
        </div>
      </div>
    </div>
  );

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
  };

  if (!loadingHighlights && (!highlights || !highlights?.properties || highlights.properties.length === 0)) {
    return null;
  }

  return (
    <div className="nc-SectionGridFeaturePlaces relative py-16">
      <div className="w-full">
        <HeaderFilter
          tabActive={activeTab}
          subHeading={subHeading}
          headingIsCenter={headingIsCenter}
          tabs={destinations as string[]}
          heading={heading}
          onClickTab={handleTabClick}
        />

        {loadingHighlights ? (
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-12 ${gridClass}`}>
            {[...Array(4)].map((_, index) => (
              <div key={index} className="relative bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-2xl overflow-hidden">
                {renderCardSkeleton()}
              </div>
            ))}
          </div>
        ) : (
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-12 ${gridClass}`}>
            {filteredProperties.slice(0, 8).map(renderCard)}
          </div>
        )}
      </div>
    </div>
  );
};

export default SectionGridFeaturePlaces;
